import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Bold, Italic, Underline, Palette, Type, Highlighter, Link, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import { GOOGLE_FONTS, loadAllGoogleFonts } from '../../utils/googleFonts';
import './RichTextEditor.css';

const RichTextEditor = forwardRef(({ value, onChange, placeholder = "Enter description...", hideToolbar = false }, ref) => {
  const editorRef = useRef(null);
  
  // Expose the editor ref to parent
  useImperativeHandle(ref, () => editorRef.current);
  
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showFontPicker, setShowFontPicker] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkData, setLinkData] = useState({ text: '', url: '' });
  const [selectedFont, setSelectedFont] = useState("'Inter', sans-serif");
  const savedSelection = useRef(null);

  // Load all Google Fonts when component mounts
  useEffect(() => {
    loadAllGoogleFonts();
  }, []);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
    // Enforce p tag as default separator to help with list isolation
    document.execCommand('defaultParagraphSeparator', false, 'p');
  }, [value]);

  // Handle content changes
  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  // Handle key down to fix list exit behavior
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        let node = selection.focusNode;
        // Traverse up to find the closest LI or stop at editor root
        while (node && node !== editorRef.current && node.nodeName !== 'LI') {
          node = node.parentNode;
        }

        if (node && node.nodeName === 'LI') {
          // Check if key is Enter and LI is effectively empty
          const textContent = node.textContent || '';
          const cleanText = textContent.replace(/[\s\u200B\u00A0\uFEFF]/g, '');
          
          if (cleanText.length === 0) {
            e.preventDefault();
            
            // DOM Manipulation method (more robust than execCommand)
            const listNode = node.parentNode; // OL or UL
            const parentNode = listNode.parentNode;
            
            // Remove the empty LI
            node.remove();
            
            // Create a new paragraph
            const p = document.createElement('p');
            p.innerHTML = '<br>'; // Needed for caret to focus
            
            // Insert paragraph after the list
            if (listNode.nextSibling) {
              parentNode.insertBefore(p, listNode.nextSibling);
            } else {
              parentNode.appendChild(p);
            }
            
            // Move cursor to new paragraph
            const range = document.createRange();
            range.setStart(p, 0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            
            // Trigger input event to update content
            handleInput();
          }
        }
      }
    }
  };

  // Format text commands
  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
  };

  // Apply font family
  const applyFont = (fontValue) => {
    setSelectedFont(fontValue);
    formatText('fontName', fontValue);
    setShowFontPicker(false);
  };

  // Apply list style (for Roman) - Fixed to work only on selected lines
  const applyListStyle = (style) => {
    const selection = window.getSelection();
    let isInsideOL = false;
    
    if (selection.rangeCount > 0) {
      let node = selection.focusNode;
      while (node && node !== editorRef.current) {
        if (node.tagName === 'OL') {
          isInsideOL = true;
          node.style.listStyleType = style;
          break;
        }
        node = node.parentNode;
      }
    }
    
    if (!isInsideOL) {
      formatText('insertOrderedList');
      setTimeout(() => {
        const selectionAfter = window.getSelection();
        if (selectionAfter.rangeCount > 0) {
          let nodeAfter = selectionAfter.focusNode;
          while (nodeAfter && nodeAfter !== editorRef.current) {
            if (nodeAfter.tagName === 'OL') {
              nodeAfter.style.listStyleType = style;
              break;
            }
            nodeAfter = nodeAfter.parentNode;
          }
        }
        handleInput();
      }, 0);
    } else {
      handleInput();
    }
  };

  // Save current selection
  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0);
    }
  };

  // Restore saved selection
  const restoreSelection = () => {
    const selection = window.getSelection();
    if (savedSelection.current) {
      selection.removeAllRanges();
      selection.addRange(savedSelection.current);
    }
  };

  // Open link modal
  const openLinkModal = () => {
    saveSelection();
    const selection = window.getSelection();
    const selectedText = selection.toString();
    setLinkData({ text: selectedText, url: '' });
    setShowLinkModal(true);
  };

  // Insert hyperlink
  const insertLink = () => {
    if (!linkData.url.trim()) {
      alert('Please enter a URL');
      return;
    }

    restoreSelection();

    // If there's custom text, use it; otherwise use the URL
    const displayText = linkData.text.trim() || linkData.url;

    // Create the link HTML
    const linkHtml = `<a href="${linkData.url}" target="_blank" rel="noopener noreferrer" style="color: #105652; text-decoration: underline;">${displayText}</a>`;

    // Insert the link
    document.execCommand('insertHTML', false, linkHtml);

    // Reset and close
    setLinkData({ text: '', url: '' });
    setShowLinkModal(false);
    editorRef.current.focus();
    handleInput();
  };

  // Color options
  const colors = [
    '#000000', '#333333', '#666666', '#999999', '#CCCCCC',
    '#FF0000', '#FF6B6B', '#FF9F43', '#FFA502', '#F39C12',
    '#2ECC71', '#27AE60', '#00D2D3', '#0ABDE3', '#3742FA',
    '#9C88FF', '#A55EEA', '#FD79A8', '#FDCB6E', '#6C5CE7',
    '#105652', '#1E8479', '#2D3436', '#636E72', '#DDD'
  ];

  // Highlight colors
  const highlightColors = [
    '#FFEB3B', '#FFC107', '#FF9800', '#FF5722', '#F44336',
    '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3',
    '#03DAC6', '#4CAF50', '#8BC34A', '#CDDC39', '#FFE0B2'
  ];

  return (
    <div className="rich-text-editor border rounded-lg overflow-hidden" style={{ borderColor: '#105652' }}>
      {/* Toolbar */}
      {!hideToolbar && (
      <div className="bg-gray-50 border-b px-3 py-2 flex items-center gap-2 flex-wrap">
        {/* Font Selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowFontPicker(!showFontPicker)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border rounded hover:bg-gray-100 transition-colors"
            title="Font Family"
          >
            <Type className="w-4 h-4" />
            Font
          </button>
          {showFontPicker && (
            <div className="font-picker absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg w-64 max-h-64 overflow-y-auto" style={{ zIndex: 10000 }}>
              <div className="p-2">
                <div className="text-xs font-semibold text-gray-500 mb-2">BANGLA SUPPORT</div>
                {GOOGLE_FONTS.filter(f => f.bangla).map(font => (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => applyFont(font.value)}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded transition-colors"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}
                
                <div className="text-xs font-semibold text-gray-500 mb-2 mt-3">SERIF FONTS</div>
                {GOOGLE_FONTS.filter(f => f.category === 'serif' && !f.bangla).slice(0, 10).map(font => (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => applyFont(font.value)}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded transition-colors"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}

                <div className="text-xs font-semibold text-gray-500 mb-2 mt-3">SANS-SERIF FONTS</div>
                {GOOGLE_FONTS.filter(f => f.category === 'sans-serif' && !f.bangla).slice(0, 10).map(font => (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => applyFont(font.value)}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded transition-colors"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}

                <div className="text-xs font-semibold text-gray-500 mb-2 mt-3">CERTIFICATE FONTS</div>
                {GOOGLE_FONTS.filter(f => f.category === 'certificate').map(font => (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => applyFont(font.value)}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded transition-colors"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}

                <div className="text-xs font-semibold text-gray-500 mb-2 mt-3">DISPLAY FONTS</div>
                {GOOGLE_FONTS.filter(f => f.category === 'display').slice(0, 8).map(font => (
                  <button
                    key={font.name}
                    type="button"
                    onClick={() => applyFont(font.value)}
                    className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded transition-colors"
                    style={{ fontFamily: font.value }}
                  >
                    {font.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-gray-300" />

        {/* Text Formatting */}
        <button
          type="button"
          onClick={() => formatText('bold')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => formatText('italic')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => formatText('underline')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={openLinkModal}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Insert Link"
        >
          <Link className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-gray-300" />

        {/* Lists */}
        <button
          type="button"
          onClick={() => formatText('insertUnorderedList')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Bullet List (Dots)"
        >
          <List className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => formatText('insertOrderedList')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Numbered List"
        >
          <ListOrdered className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => applyListStyle('upper-roman')}
          className="flex items-center gap-1 px-2 py-1 hover:bg-gray-200 rounded transition-colors text-[10px] font-bold"
          title="Roman Numeral List"
        >
          <ListOrdered className="w-4 h-4" />
          <span>I, II...</span>
        </button>

        <div className="w-px h-6 bg-gray-300" />

        {/* Alignment */}
        <button
          type="button"
          onClick={() => formatText('justifyLeft')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Align Left"
        >
          <AlignLeft className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => formatText('justifyCenter')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Align Center"
        >
          <AlignCenter className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => formatText('justifyRight')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Align Right"
        >
          <AlignRight className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={() => formatText('justifyFull')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Justify"
        >
          <AlignJustify className="w-4 h-4" />
        </button>

        <div className="w-px h-6 bg-gray-300" />

        {/* Text Color */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Text Color"
          >
            <Palette className="w-4 h-4" />
          </button>
          {showColorPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg p-3" style={{ zIndex: 10000 }}>
              <div className="text-xs font-semibold text-gray-500 mb-2">TEXT COLOR</div>
              <div className="grid grid-cols-5 gap-1 w-40">
                {colors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      formatText('foreColor', color);
                      setShowColorPicker(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Highlight Color */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowHighlightPicker(!showHighlightPicker)}
            className="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-200 rounded transition-colors"
            title="Highlight"
          >
            <Highlighter className="w-4 h-4" />
          </button>
          {showHighlightPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg p-3" style={{ zIndex: 10000 }}>
              <div className="text-xs font-semibold text-gray-500 mb-2">HIGHLIGHT</div>
              <div className="grid grid-cols-5 gap-1 w-40">
                {highlightColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => {
                      formatText('backColor', color);
                      setShowHighlightPicker(false);
                    }}
                    className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className={`p-4 focus:outline-none ${hideToolbar ? 'min-h-[530px]' : 'min-h-[200px]'}`}
        style={{ 
          fontFamily: selectedFont,
          lineHeight: '1.6'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#105652' }}>
              Insert Link
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Display Text</label>
                <input
                  type="text"
                  value={linkData.text}
                  onChange={(e) => setLinkData({ ...linkData, text: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#105652' }}
                  placeholder="Text to display (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to use the URL as display text
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">URL *</label>
                <input
                  type="url"
                  value={linkData.url}
                  onChange={(e) => setLinkData({ ...linkData, url: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                  style={{ borderColor: '#105652' }}
                  placeholder="https://example.com"
                  required
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={insertLink}
                className="flex-1 px-4 py-2 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#105652' }}
              >
                Insert Link
              </button>
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkData({ text: '', url: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default RichTextEditor;